import type {
  IChartApi,
  IPrimitivePaneRenderer,
  ISeriesApi,
  ISeriesPrimitive,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
  SeriesAttachedParameter,
  SeriesType,
  Time,
  UTCTimestamp,
} from 'lightweight-charts';

type RenderTarget = Parameters<NonNullable<IPrimitivePaneRenderer['drawBackground']>>[0];

/** One swing with its levels, as drawn. Several share a chart. */
export interface FibDrawing {
  /** Short name shown in every label, e.g. the timeframe. */
  tag: string;
  color: string;
  direction: 'up' | 'down';
  start: { time: number; price: number };
  end: { time: number; price: number };
  levels: { ratio: number; price: number }[];
  digits: number;
  /** Confirmed pivots leading up to the swing, drawn as a thin dotted path. */
  path?: { time: number; price: number }[];
}

/** The levels drawn solid and a touch heavier; the rest dashed. */
const EMPHASISED = new Set([0, 0.118, 0.559, 0.702, 1]);

function ratioLabel(r: number): string {
  return r.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}

/**
 * Draws Fibonacci retracements on swings: each swing as a diagonal between
 * its two pivots, and each ratio as a horizontal line running from the
 * swing's start to the right edge, labelled with the swing's tag, the ratio
 * and the price.
 *
 * Lines are rays from the swing's END: a retracement level only means
 * something once the move it retraces is over, and drawing it back across
 * the swing itself only hides the candles that made it.
 */
export class FibPrimitive implements ISeriesPrimitive<Time> {
  private chart: IChartApi | null = null;
  private series: ISeriesApi<SeriesType> | null = null;
  private requestUpdate: (() => void) | null = null;
  private drawings: FibDrawing[] = [];
  /** The chart's bar open times, ascending, for snapping finer times to bars. */
  private barTimes: number[] = [];
  private readonly view: IPrimitivePaneView;

  constructor() {
    const renderer: IPrimitivePaneRenderer = {
      drawBackground: (target: RenderTarget) => this.render(target),
      draw: () => {},
    };
    this.view = {
      zOrder: (): PrimitivePaneViewZOrder => 'bottom',
      renderer: () => renderer,
    };
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this.chart = param.chart as IChartApi;
    this.series = param.series as ISeriesApi<SeriesType>;
    this.requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this.chart = null;
    this.series = null;
    this.requestUpdate = null;
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return [this.view];
  }

  setDrawings(drawings: FibDrawing[]): void {
    this.drawings = drawings;
    this.requestUpdate?.();
  }

  setBarTimes(times: number[]): void {
    this.barTimes = times;
    this.requestUpdate?.();
  }

  /**
   * The x of a time, snapped to the bar that contains it.
   *
   * Swing points are refined on finer bars, so their times usually fall
   * inside a chart bar rather than on its open. `timeToCoordinate` answers
   * null for such a time and the swing silently vanished from the coarser
   * charts — the owner's "can't see its start and end" on D1.
   */
  private xOf(timeScale: ReturnType<IChartApi['timeScale']>, time: number): number | null {
    const direct = timeScale.timeToCoordinate(time as UTCTimestamp);
    if (direct !== null) return direct;
    const times = this.barTimes;
    if (times.length === 0) return null;
    // Last bar whose open is <= time.
    let lo = 0;
    let hi = times.length - 1;
    if (time < times[0]!) return timeScale.timeToCoordinate(times[0] as UTCTimestamp);
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (times[mid]! <= time) lo = mid;
      else hi = mid - 1;
    }
    return timeScale.timeToCoordinate(times[lo] as UTCTimestamp);
  }

  private render(target: RenderTarget): void {
    const chart = this.chart;
    const series = this.series;
    if (!chart || !series || this.drawings.length === 0) return;

    const timeScale = chart.timeScale();

    target.useBitmapCoordinateSpace(({ context, bitmapSize, horizontalPixelRatio: hr, verticalPixelRatio: vr }) => {
      const width = bitmapSize.width;
      const fontPx = Math.round(10.5 * vr);
      context.font = `500 ${fontPx}px ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
      context.textBaseline = 'bottom';

      // Labels are stacked per x-origin so two swings starting off-screen
      // (both clamped to x=0) do not print on top of each other: each
      // drawing's labels are offset by its index among those sharing the edge.
      let clampedIndex = 0;

      for (const d of this.drawings) {
        const xStart = this.xOf(timeScale, d.start.time);
        const xEnd = this.xOf(timeScale, d.end.time);
        const yStart = series.priceToCoordinate(d.start.price);
        const yEnd = series.priceToCoordinate(d.end.price);

        const clamped = xEnd === null || xEnd < 0;
        const lineLeft = clamped ? 0 : xEnd * hr;
        const labelX = lineLeft + 6 * hr + (clamped ? clampedIndex * 92 * hr : 0);
        if (clamped) clampedIndex++;

        for (const level of d.levels) {
          const y = series.priceToCoordinate(level.price);
          if (y === null) continue;
          const py = Math.round(y * vr) + 0.5;
          if (py < -fontPx || py > bitmapSize.height + fontPx) continue;

          const strong = EMPHASISED.has(level.ratio);
          context.save();
          context.strokeStyle = d.color;
          context.globalAlpha = strong ? 0.9 : 0.5;
          context.lineWidth = Math.max(1, Math.round((strong ? 1.25 : 1) * hr));
          if (!strong) {
            const dash = 4 * hr;
            context.setLineDash([dash, dash]);
          }
          context.beginPath();
          context.moveTo(lineLeft, py);
          context.lineTo(width, py);
          context.stroke();
          context.restore();

          context.fillStyle = d.color;
          context.globalAlpha = strong ? 1 : 0.8;
          context.fillText(`${d.tag} ${ratioLabel(level.ratio)}  ${level.price.toFixed(d.digits)}`, labelX, py - 2 * vr);
          context.globalAlpha = 1;
        }

        // The pivot path: how the detector got here.
        if (d.path && d.path.length > 1) {
          context.save();
          context.strokeStyle = d.color;
          context.globalAlpha = 0.45;
          context.lineWidth = Math.max(1, Math.round(hr));
          context.setLineDash([2 * hr, 3 * hr]);
          context.beginPath();
          let started = false;
          for (const p of d.path) {
            const x = this.xOf(timeScale, p.time);
            const y = series.priceToCoordinate(p.price);
            if (x === null || y === null) continue;
            if (!started) {
              context.moveTo(x * hr, y * vr);
              started = true;
            } else {
              context.lineTo(x * hr, y * vr);
            }
          }
          context.stroke();
          context.restore();
        }

        if (xStart !== null && xEnd !== null && yStart !== null && yEnd !== null) {
          const x1 = xStart * hr;
          const y1 = yStart * vr;
          const x2 = xEnd * hr;
          const y2 = yEnd * vr;
          context.save();
          context.strokeStyle = d.color;
          context.fillStyle = d.color;
          context.lineWidth = Math.max(1, Math.round(1.5 * hr));
          context.beginPath();
          context.moveTo(x1, y1);
          context.lineTo(x2, y2);
          context.stroke();
          const r = 3 * hr;
          for (const [x, y] of [[x1, y1], [x2, y2]] as const) {
            context.beginPath();
            context.arc(x, y, r, 0, Math.PI * 2);
            context.fill();
          }
          context.restore();
        }
      }
    });
  }
}
